import React, { useEffect, useRef } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import axios from "axios";
import ReactTooltip from "react-tooltip";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "./state/index";
import { toast } from "react-toastify";
import { useHistory } from "react-router";
import { ThemeProvider } from "styled-components";
import { useSelector } from "react-redux";
import { useTour } from "./components/tutorial/TourProvider";

// import { MobileView } from "react-device-detect";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { GlobalStyles } from "./GlobalStyles";
import { themeDark, themeLight } from "./theme";
import Toastify from "./components/Toastify";
import "./sass/style.scss";
import ProtectedRoute from "./components/protected/ProtectedRoute";
import ProtectedLayout from "./components/protected/ProtectedLayout";
import { API_ENDPOINTS } from "./config/api";
import setupTranslationInterceptor from "./config/axios";
import CookieConsent from "./components/CookieConsent";

// Pages
import HowToStream from "./pages/HowToStream";
import TermsAndConditions from "./pages/TermsAndConditions";
import Game from "./pages/Game";
import Tag from "./pages/Tag";
import Home from "./pages/Home";
import Search from "./pages/Search";
import Stream from "./pages/Stream";
import Components from "./pages/Components";
import ResetPassword from "./pages/ResetPassword";
import NewPassword from "./pages/NewPassword";
import Settings from "./pages/Settings";
// import Participants from "./pages/Participants";
import NotFound from "./pages/NotFound";
import Profile from "./pages/Profile";
// Payment Pages
import GCoinStore from "./pages/GCoinStore";
import GCoinTransactions from "./pages/GCoinTransactions";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
// import TournamentDashboard from "./pages/TournamentDashboard";
// import TournamentSettings from "./pages/TournamentSettings";
import StreamPreview from "./pages/StreamPreview";
import Notificationpage from "./pages/Notificationpage";
import Loading from "./components/animations.js"
import Reversements from "./pages/Reversements";
// Event Pages
import Events from "./pages/Events";
import EventDetails from "./pages/EventDetails";
import CreateEvent from "./pages/CreateEvent";
import EventDashboard from "./pages/EventDashboard";
import MyEvents from "./pages/MyEvents";
import StreamerDashboard from "./pages/StreamerDashboard";
import TournamentEvents from "./pages/TournamentEvents";
import EventWatch from "./pages/EventWatch";
import MatchDetails from "./pages/MatchDetails";
// RBAC Components
import RoleDashboard from "./components/RoleDashboard";
// Legal Pages
import RGPD from "./pages/RGPD";
import CGU from "./pages/CGU";
import FAQ from "./pages/FAQ";
import MentionsLegales from "./pages/MentionsLegales";
import CookieSettingsPage from "./pages/CookieSettingsPage";
import Contact from "./pages/Contact";
// Layouts
import Navbar from "./layout/Navbar";
// import TabBar from "./layout/TabBar";
import Sidebar from "./layout/Sidebar";
import VideoPage from "./pages/VideoPage";
import ReplayPlayer from "./pages/ReplayPlayer";
import Chat_ from "./components/Chat";
import TranslationTest from "./pages/TranslationTest";
import ErrorBoundary from "./components/ErrorBoundary";

const App = () => {
  const { startTour } = useTour();
  const tourStartedRef = useRef(false);

  const TOUR_COMPLETED_KEY = "corearena.tour.completed";
  const TOUR_PENDING_KEY = "corearena.tour.pending";

  // Default theme is light mode
  let browserTheme = false;
  // Check Browser Theme
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  )
    browserTheme = true;
  // Check local Storage if there is a theme already setted up, will overite browser theme
  if (Boolean(localStorage.getItem("dark-mode"))) browserTheme = true;
  // Make default theme depending on browser and localStorage
  const [theme, setTheme] = useLocalStorage("dark-mode", browserTheme);

  // Setup translation interceptor on mount
  useEffect(() => {
    setupTranslationInterceptor();
  }, []);

  // Dispatch LocalStorage whene theme changes
  useEffect(() => {
    const themeChangedEvent = new CustomEvent("darkmode");
    themeChangedEvent.data = theme;
    window.dispatchEvent(themeChangedEvent);
  }, [theme]);

  const currentTheme = theme ? themeDark : themeLight;
  const [toggleSidebar, setToggleSidebar] = useLocalStorage(
    "sidebarCollapsed",
    false
  );
  const currentPage = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { login, logout } = bindActionCreators(actionCreators, dispatch);
  const search = useLocation().search;

  useEffect(() => {
    const query = new URLSearchParams(search);
    const res = query.get("oauthsuccess");
    const firstLogin = query.get("firstlogin");
    if (res && firstLogin) {
      if (res === "true") {
        axios(API_ENDPOINTS.AUTH_ME, {
          method: "get",
          data: {},
          withCredentials: true,
        })
          .then((res) => {
            // Accept both nested and flat user payloads
            login(res.data.user || res.data);
            toast.success("Login Success");
          })
          .catch((error) => {
            if (error?.response?.status === 401) logout();
            else if (error.response) {
              if (error?.response?.status === 403) {
                toast.info("Your session expired.");
                logout();
              }
            } else if (error.request)
              toast.error("We are experiencing service outages.");
            else
              toast.error(
                "Something is blocking you from accessing our servers."
              );
          });
      }
      if (firstLogin === "true") {
        // Trigger onboarding tour on next render after redirect
        try {
          localStorage.setItem(TOUR_PENDING_KEY, "1");
        } catch (_) {}

        axios
          .post(
            API_ENDPOINTS.INIT_CHANNEL,
            {},
            { withCredentials: true }
          )
          .then((res) => {
            toast(res.data.message);
          })
          .catch((error) => {
            if (error?.response?.status === 401) logout();
            else if (error.response) {
              if (error?.response?.status === 422)
                toast.info("channel already created.");
              if (error?.response?.status === 500)
                toast.error("Internal server error.");
            } else if (error.request)
              toast.error("We are experiencing service outages.");
            else
              toast.error(
                "Something is blocking you from accessing our servers."
              );
          });
      }
      history.push("/");
    }
  }, [history, login, search, logout]);

  //Dispatch Logout whene is not checked remember me
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector(state => state.isLoading.data)
  const userData = useSelector((state) => state.user.data);
  // console.log(isLoading)
  useEffect(() => {
    if (isLoggedIn) {
      axios(API_ENDPOINTS.AUTH_ME, {
        method: "get",
        data: {},
        withCredentials: true,
      }).catch((err) => {
        if (err.response?.status === 401) logout();
      });
    }
  }, [logout, isLoggedIn]);

  // Post-login redirect (used for guest flows like "click live video -> login -> resume").
  useEffect(() => {
    if (!isLoggedIn) return;
    try {
      const redirect = localStorage.getItem("postLoginRedirect");
      if (!redirect) return;
      // Only allow internal redirects
      if (typeof redirect === "string" && redirect.startsWith("/")) {
        localStorage.removeItem("postLoginRedirect");
        history.replace(redirect);
      } else {
        localStorage.removeItem("postLoginRedirect");
      }
    } catch (_) {}
  }, [isLoggedIn, history]);

  // Toggle Sidebar
  const handleToggleSidebar = () => {
    setToggleSidebar(() => !toggleSidebar);
  };
  const username = useSelector((state) => state.user.data.username);
  
  // Pages that should not show navbar/sidebar
  const pageName = window.location.pathname.split("/")[1];
  const standalonePages = ["reset-password", "new-password"];
  const isStandalonePage = standalonePages.includes(pageName);

  useEffect(() => {
    if (tourStartedRef.current) return;
    if (!isLoggedIn) return;
    if (isStandalonePage) return;

    const isStreamChat = window.location.pathname.startsWith("/streamChat/");
    if (isStreamChat) return;

    let completed = false;
    let pending = false;
    let completionKeyExists = false;
    try {
      completed = localStorage.getItem(TOUR_COMPLETED_KEY) === "1";
      pending = localStorage.getItem(TOUR_PENDING_KEY) === "1";
      completionKeyExists = localStorage.getItem(TOUR_COMPLETED_KEY) !== null;
    } catch (_) {}

    if (completed) return;
    // Start if we have an explicit pending marker OR user has never completed/skipped before.
    if (!pending && completionKeyExists) return;

    tourStartedRef.current = true;
    // Delay start slightly to ensure Navbar/Sidebar DOM nodes are mounted
    // (prevents Joyride from immediately skipping all steps on slow renders).
    setTimeout(() => startTour(), 250);
  }, [isLoggedIn, isStandalonePage, startTour]);

  return (
    <ThemeProvider theme={currentTheme}>
      <ReactTooltip />
      <GlobalStyles />
      <Toastify />
      <CookieConsent />
      {!isStandalonePage && (
        <ProtectedLayout
          component={Sidebar}
          isCollapsed={toggleSidebar}
          currentPage={currentPage.pathname}
        />
      )}
      {(window.location.pathname === "/streamChat/" + username) ?
        <Route exact path="/streamChat/:username" component={Chat_} />
        :
        <div >
          {!isStandalonePage && (
            <Navbar
              theme={currentTheme}
              toggleSidebar={handleToggleSidebar}
              changeTheme={() => setTheme(() => !theme)}
            />
          )}
          <main style={{ position: "relative" }}>
            {isLoading ? <Loading /> :
              <ErrorBoundary>
                <Switch>
                  <Route exact path="/" component={Home} />
                  {/* <Route exact path="/corearena-app" component={Home} /> */}
                  <Route exact path="/components" component={Components} />
                  <Route exact path="/translation-test" component={TranslationTest} />
                  <Route exact path="/privacy-policy">
                    <Redirect to="/legal/rgpd" />
                  </Route>
                  <Route
                    exact
                    path="/terms-and-conditions"
                    component={TermsAndConditions}
                  />
                  {/* Legal Routes */}
                  <Route exact path="/contact" component={Contact} />
                  <Route exact path="/legal/rgpd" component={RGPD} />
                  <Route exact path="/legal/cgu" component={CGU} />
                  <Route exact path="/legal/faq" component={FAQ} />
                  <Route exact path="/legal/mentions-legales" component={MentionsLegales} />
                  <Route exact path="/settings/cookies" component={CookieSettingsPage} />
                  <Route exact path="/how-to-stream" component={HowToStream} />
                  {/* Event Routes */}
                  <Route exact path="/events" component={Events} />
                  <Route exact path="/events/tournaments" component={TournamentEvents} />
                  <Route exact path="/events/create" component={CreateEvent} />
                  <ProtectedRoute exact path="/my-events" component={MyEvents} />
                  <Route exact path="/events/:slug/watch" component={EventWatch} />
                  <Route exact path="/events/:slug/match/:matchId" component={MatchDetails} />
                  <Route exact path="/events/:slug" component={EventDetails} />
                  <ProtectedRoute exact path="/events/:slug/dashboard" component={EventDashboard} />
                  <ProtectedRoute exact path="/stream-dashboard" component={StreamerDashboard} />
                  {/* Payment Routes */}
                  <Route exact path="/gcoin-store">
                    <GCoinStore currentUser={userData} />
                  </Route>
                  <Route exact path="/gcoin-transactions" component={GCoinTransactions} />
                  <ProtectedRoute exact path="/reversements" component={Reversements} />
                  <Route exact path="/payment/success" component={PaymentSuccess} />
                  <Route exact path="/payment/cancel" component={PaymentCancel} />
                  {/* RBAC Dashboard */}
                  <ProtectedRoute exact path="/dashboard" component={RoleDashboard} />
                  <Route exact path="/profile" component={Profile} />
                  <Route exact path="/profile/:username" component={Profile} />
                  <Route exact path="/new-password/:token" component={NewPassword} />
                  <Route exact path="/reset-password" component={ResetPassword} />
                  <ProtectedRoute exact path="/settings" component={Settings} />
                  <Route exact path="/game/:id" component={Game} />
                  <Route exact path="/tag/:tagName" component={Tag} />
                  <Route exact path="/search/:id" component={Search} />
                  <Route exact path="/video/1" component={VideoPage} />
                  <Route exact path="/stream/:username" component={Stream} />
                  <Route exact path="/replay/:replayId" component={ReplayPlayer} />
                  <Route exact path="/notifications" component={Notificationpage} />
                  <ProtectedRoute exact path="/preview" component={StreamPreview} />
                  <Route path="" component={NotFound} />
                </Switch>
              </ErrorBoundary>
            }
          </main>
        </div>
      }
    </ThemeProvider>
  );
};
export default App;

// import React, { useEffect } from "react";
// import { Route, Switch, useLocation } from "react-router-dom";
// import axios from "axios";
// import ReactTooltip from "react-tooltip";
// import { useDispatch } from "react-redux";
// import { bindActionCreators } from "redux";
// import { actionCreators } from "./state/index";
// import { toast } from "react-toastify";
// import { useHistory } from "react-router";
// import { ThemeProvider } from "styled-components";
// import { useSelector } from "react-redux";

// // import { MobileView } from "react-device-detect";
// import { useLocalStorage } from "./hooks/useLocalStorage";
// import { GlobalStyles } from "./GlobalStyles";
// import { themeDark, themeLight } from "./theme";
// import Toastify from "./components/Toastify";
// import "./sass/style.scss";
// import ProtectedRoute from "./components/protected/ProtectedRoute";
// import ProtectedLayout from "./components/protected/ProtectedLayout";
// import { API_ENDPOINTS } from "./config/api";

// // Pages
// import Tournaments from "./pages/Tournaments";
// import HowToStream from "./pages/HowToStream";
// import PrivacyPolicy from "./pages/PrivacyPolicy";
// import TermsAndConditions from "./pages/TermsAndConditions";
// import Game from "./pages/Game";
// import Tag from "./pages/Tag";
// import Home from "./pages/Home";
// import Search from "./pages/Search";
// import Stream from "./pages/Stream";
// import TournamentDetails from "./pages/TournamentDetails";
// import Components from "./pages/Components";
// import ResetPassword from "./pages/ResetPassword";
// import NewPassword from "./pages/NewPassword";
// import Settings from "./pages/Settings";
// import MyTournaments from "./pages/MyTournaments";
// import MyTournamentsArchive from "./pages/MyTournamentsArchive";
// import Participants from "./pages/Participants";
// import CreateTournament from "./pages/CreateTournament";
// import NotFound from "./pages/NotFound";
// import Profile from "./pages/Profile";
// import TournamentDashboard from "./pages/TournamentDashboard";
// import TournamentSettings from "./pages/TournamentSettings";
// import StreamPreview from "./pages/StreamPreview";
// import Notificationpage from "./pages/Notificationpage";
// import Loading from "./components/animations.js"
// // Layouts
// import Navbar from "./layout/Navbar";
// // import TabBar from "./layout/TabBar";
// import Sidebar from "./layout/Sidebar";
// import VideoPage from "./pages/VideoPage";
// import Chat_ from "./components/Chat";

// const App = () => {
//   // Default theme is light mode
//   let browserTheme = false;
//   // Check Browser Theme
//   if (
//     window.matchMedia &&
//     window.matchMedia("(prefers-color-scheme: dark)").matches
//   )
//     browserTheme = true;
//   // Check local Storage if there is a theme already setted up, will overite browser theme
//   if (Boolean(localStorage.getItem("dark-mode"))) browserTheme = true;
//   // Make default theme depending on browser and localStorage
//   const [theme, setTheme] = useLocalStorage("dark-mode", browserTheme);

//   // Dispatch LocalStorage whene theme changes
//   useEffect(() => {
//     const themeChangedEvent = new CustomEvent("darkmode");
//     themeChangedEvent.data = theme;
//     window.dispatchEvent(themeChangedEvent);
//   }, [theme]);

//   const currentTheme = theme ? themeDark : themeLight;
//   const [toggleSidebar, setToggleSidebar] = useLocalStorage(
//     "sidebarCollapsed",
//     false
//   );
//   const currentPage = useLocation();
//   const history = useHistory();
//   const dispatch = useDispatch();
//   const { login, logout } = bindActionCreators(actionCreators, dispatch);
//   const search = useLocation().search;

//   useEffect(() => {
//     const query = new URLSearchParams(search);
//     const res = query.get("oauthsuccess");
//     const firstLogin = query.get("firstlogin");
//     if (res && firstLogin) {
//       if (res === "true") {
//         axios(API_ENDPOINTS.AUTH_ME, {
//           method: "get",
//           withCredentials: true,
//         })
//           .then((res) => {
//             login(res.data);
//             toast.success("Login Success");
//           })
//           .catch((error) => {
//             if (error?.response?.status === 401) logout();
//             else if (error.response) {
//               if (error?.response?.status === 403) {
//                 toast.info("Your session expired.");
//                 logout();
//               }
//             } else if (error.request)
//               toast.error("We are experiencing service outages.");
//             else
//               toast.error(
//                 "Something is blocking you from accessing our servers."
//               );
//           });
//       }
//       if (firstLogin === "true") {
//         axios
//           .post(
//             API_ENDPOINTS.INIT_CHANNEL,
//             {},
//             { withCredentials: true }
//           )
//           .then((res) => {
//             toast(res.data.message);
//           })
//           .catch((error) => {
//             if (error?.response?.status === 401) logout();
//             else if (error.response) {
//               if (error?.response?.status === 422)
//                 toast.info("channel already created.");
//               if (error?.response?.status === 500)
//                 toast.error("Internal server error.");
//             } else if (error.request)
//               toast.error("We are experiencing service outages.");
//             else
//               toast.error(
//                 "Something is blocking you from accessing our servers."
//               );
//           });
//       }
//       history.push("/");
//     }
//   }, [history, login, search, logout]);

//   //Dispatch Logout whene is not checked remember me
//   const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
//   const isLoading = useSelector(state => state.isLoading.data)
//   const userData = useSelector((state) => state.user.data);

//   // Debug logs
//   console.log('🔍 [APP] État actuel:');
//   console.log('   - isLoggedIn:', isLoggedIn);
//   console.log('   - isLoading:', isLoading);
//   console.log('   - userData:', userData);
//   console.log('   - username:', userData?.username);

//   // Charger les données utilisateur au démarrage (une seule fois)
//   useEffect(() => {
//     console.log('🚀 [APP] Démarrage - Chargement des données utilisateur...');
//     console.log('🔗 [APP] URL appelée:', API_ENDPOINTS.AUTH_ME);

//     // Éviter de faire l'appel si on est déjà en train de charger
//     if (isLoading) {
//       console.log('⏳ [APP] Déjà en cours de chargement, skip...');
//       return;
//     }

//     axios(API_ENDPOINTS.AUTH_ME, {
//       method: "get",
//       withCredentials: true,
//     })
//       .then((res) => {
//         console.log('✅ [APP] Données utilisateur récupérées:', res.data);
//         console.log('👤 [APP] User:', res.data);
//         console.log('📊 [APP] Type de res.data:', typeof res.data);
//         console.log('🔍 [APP] Clés de res.data:', Object.keys(res.data || {}));

//         // Le backend renvoie directement l'objet utilisateur, pas res.data.user
//         if (res.data && res.data.id) {
//           console.log('✅ [APP] Données utilisateur valides, login...');
//           login(res.data);
//         } else {
//           console.log('❌ [APP] Données utilisateur invalides:', res.data);
//         }
//       })
//       .catch((err) => {
//         console.log('❌ [APP] Erreur lors du chargement des données:', err);
//         console.log('❌ [APP] Status:', err.response?.status);
//         console.log('❌ [APP] Data:', err.response?.data);
//         if (err.response?.status === 401) {
//           console.log('🔒 [APP] Utilisateur non connecté - Logout automatique');
//           logout();
//         }
//       });
//   }, []); // Dépendances vides = exécuté une seule fois au montage

//   // Toggle Sidebar
//   const handleToggleSidebar = () => {
//     setToggleSidebar(() => !toggleSidebar);
//   };
//   const username = useSelector((state) => state.user.data?.username);

//   return (
//     <ThemeProvider theme={currentTheme}>
//       <ReactTooltip />
//       <GlobalStyles />
//       <Toastify />
//       <ProtectedLayout
//         component={Sidebar}
//         isCollapsed={toggleSidebar}
//         currentPage={currentPage.pathname}
//       />
//       {(window.location.pathname === "/streamChat/" + username) ?
//         <Route exact path="/streamChat/:username" component={Chat_} />
//         :
//         <div >
//           <Navbar
//             theme={currentTheme}
//             toggleSidebar={handleToggleSidebar}
//             changeTheme={() => setTheme(() => !theme)}
//           />
//           <main style={{ position: "relative" }}>
//             {isLoading ? <Loading /> :
//               <Switch>
//                 <Route exact path="/" component={Home} />
//                 <Route exact path="/components" component={Components} />
//                 <Route exact path="/privacy-policy" component={PrivacyPolicy} />
//                 <Route
//                   exact
//                   path="/terms-and-conditions"
//                   component={TermsAndConditions}
//                 />
//                 <Route exact path="/how-to-stream" component={HowToStream} />
//                 <Route exact path="/Tournaments" component={Tournaments} />
//                 <Route exact path="/profile" component={Profile} />
//                 <Route exact path="/profile/:username" component={Profile} />
//                 <ProtectedRoute
//                   exact
//                   path="/my-tournaments"
//                   component={MyTournaments}
//                 />
//                 <ProtectedRoute
//                   exact
//                   path="/my-tournaments-archive"
//                   component={MyTournamentsArchive}
//                 />
//                 <ProtectedRoute
//                   exact
//                   path="/create-tournament"
//                   component={CreateTournament}
//                 />
//                 <Route exact path="/new-password/:token" component={NewPassword} />
//                 <Route exact path="/reset-password" component={ResetPassword} />
//                 <ProtectedRoute exact path="/settings" component={Settings} />
//                 <Route exact path="/game/:id" component={Game} />
//                 <Route exact path="/tag/:tagName" component={Tag} />
//                 <Route exact path="/search/:id" component={Search} />
//                 <Route exact path="/video/1" component={VideoPage} />
//                 <Route exact path="/stream/:username" component={Stream} />
//                 <Route exact path="/tournament/:id" component={TournamentDetails} />
//                 <Route exact path="/notifications" component={Notificationpage} />
//                 <ProtectedRoute
//                   exact
//                   path="/dashboard/participants/:id"
//                   component={Participants}
//                 />
//                 <ProtectedRoute
//                   exact
//                   path="/dashboard/settings/:id"
//                   component={TournamentSettings}
//                 />
//                 <ProtectedRoute
//                   exact
//                   path="/dashboard/:id"
//                   component={TournamentDashboard}
//                 />
//                 <ProtectedRoute exact path="/preview" component={StreamPreview} />
//                 <Route path="" component={NotFound} />
//               </Switch>
//             }
//           </main>
//         </div>
//       }
//     </ThemeProvider>
//   );
// };
// export default App;
